
import { Button, CreateButton, Drawer, ExportButton, Icons, List, Space, useTable } from "@pankod/refine-antd";
import { CanAccess, CrudFilters, HttpError, IResourceComponentsProps, useExport, useOne } from "@pankod/refine-core";
import { RenderSpinner, RenderWorkflowModal } from "components/common";
import { SawAssetFilter } from "components/sawasset/sawAssetFilter";
import { ISawAsset, ISawAssetMetadata, IState } from "interfaces";
import { useEffect, useState } from "react";
import { DATAPROVIDER_CREATE, DATAPROVIDER_EXPORT, DATAPROVIDER_LOOKUP, DATAPROVIDER_READ, RESOURCE_PATH, STALE_DURATION } from "scripts/site";
import { ListSawAssetTable } from "./listAsset2Table";

const resourcePath = RESOURCE_PATH.SAW_ASSET;
export const SawAssetList: React.FC<IResourceComponentsProps> = () => {

  const { data: entityMetadata, isLoading } = useOne<ISawAssetMetadata>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.METADATA,
    id: RESOURCE_PATH.SAW_ASSET,
    queryOptions: {
      enabled: true,
      staleTime: STALE_DURATION
    }
  });

  const entityStates = entityMetadata?.data?.states as IState[] || []; // Assign it directly
  const metaConfig = entityMetadata?.data?.config;


  const [filterOpen, switchFilterForm] = useState(false);
  const [fetchData, switchFetchData] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      switchFetchData(true);
    }
  }, [isLoading]);


  // Table and filter logic (matches codebase style)
  const {
    tableProps,
    tableQueryResult,
    searchFormProps,
    sorter,
    filters,
  } = useTable<ISawAsset, HttpError, any>({
    dataProviderName: DATAPROVIDER_READ,
    resource: resourcePath,
    initialSorter: [
      {
        field: "updatedAt",
        order: "desc",
      },
    ],
    // Filter mapping: concise, array-driven, codebase style
    onSearch: (params) => {
      // Map filter fields to backend query
      const {
        q,
        sawAssetId,
        serialNumber,
        assignedTo,
        state,
        dateOfDelivery,
        makeModel,
        dockSerialNumber,
        tcsEmployeeId,
        associateLocation,
      } = params;

      const normalizedDate =
        dateOfDelivery && typeof dateOfDelivery === "object" && typeof dateOfDelivery?.format === "function"
          ? dateOfDelivery.format("YYYY-MM-DD")
          : dateOfDelivery;
      const normalizeMultiValue = (value: unknown) => {
        if (Array.isArray(value)) {
          if (!value.length) {
            return undefined;
          }
          return value.length === 1 ? value[0] : value;
        }
        return value;
      };
      const normalizedState = normalizeMultiValue(state);
      const normalizedMakeModel = normalizeMultiValue(makeModel);

      const filters: CrudFilters = [
        { field: "q", operator: "contains", value: q },
        //{ field: "sawAssetId", operator: "eq", value: "numtostring-" + sawAssetId },
        { field: "sawAssetId", operator: "contains", value: sawAssetId },
        { field: "serialNumber", operator: "contains", value: serialNumber },
        { field: "stateManager.assignedTo ARRAY_CONTAINS", operator: "eq", value: assignedTo },
        { field: "stateManager.state", operator: "eq", value: normalizedState },
        { field: "dateOfDelivery", operator: "eq", value: normalizedDate },
        { field: "makeModel", operator: "eq", value: normalizedMakeModel },
        { field: "dockSerialNumber", operator: "contains", value: dockSerialNumber },
        { field: "tcsEmployeeId", operator: "eq", value: tcsEmployeeId },
        { field: "associateLocation", operator: "eq", value: associateLocation },
      ];
      return filters;
    },
    queryOptions: {
      enabled: fetchData,
    },
  });

  const { triggerExport, isLoading: exportLoading } = useExport<ISawAsset>({
    dataProviderName: DATAPROVIDER_EXPORT,
    resourceName: RESOURCE_PATH.SAW_ASSET,
    pageSize: metaConfig?.export?.pageSize ?? 100,
    sorter: [
      {
        field: "updatedAt",
        order: "desc",
      },
    ],
  });


  // Determine if any filters are active
  const isFiltered =
    filters &&
    filters.length > 0 &&
    filters.some((fltr) => fltr.value !== undefined);


  // Clear all filters and reset form
  const clearFilters = () => {
    searchFormProps.form?.resetFields();
    searchFormProps.form?.submit();
  };


  // Compute table title
  const totalAssets = tableQueryResult?.data?.total ?? 0;
  const computedTitle =
    (tableQueryResult.isLoading ? "Loading" : totalAssets) +
    ` SAW Asset` +
    (totalAssets === 1 ? "" : "s");


  return (
    <List
      headerProps={{
        title: computedTitle,
        tags: (
          <>
            <Button
              key="filter"
              type="default"
              icon={isFiltered ? <Icons.FilterFilled /> : <Icons.FilterOutlined />}
              style={{ paddingRight: isFiltered ? 4 : 15 }}
              title={
                isFiltered
                  ? "You are viewing a filtered list"
                  : metaConfig?.search?.textMessages?.buttonText || "Search & Filter"
              }
              onClick={() => switchFilterForm(true)}
            >
              {metaConfig?.search?.textMessages?.buttonText || "Search"}
              {isFiltered && (
                <Button
                  type="text"
                  danger
                  title="Clear Filters"
                  size="small"
                  style={{ marginLeft: 5 }}
                  onClick={(e) => {
                    clearFilters();
                    e.stopPropagation();
                  }}
                  icon={<Icons.CloseOutlined />}
                />
              )}
            </Button>
          </>
        ),
        extra: (
          <Space>
            {/* <SawAssetImport /> */}
            <CanAccess resource={RESOURCE_PATH.SAW_ASSET} action="create" params={{ id: "1", dataProviderName: DATAPROVIDER_CREATE }}>
              <CreateButton icon={<Icons.PlusOutlined />}>Add SAW Asset</CreateButton>
            </CanAccess>
            {metaConfig?.export?.enabled && (
              <ExportButton onClick={triggerExport} loading={exportLoading} />
            )}
            {RenderWorkflowModal(entityStates)}
          </Space>
        ),
      }}
    >
      {isLoading || tableQueryResult.isLoading ? (
        <RenderSpinner message="Loading..." />
      ) : (
        <ListSawAssetTable
          metadata={entityMetadata?.data}
          sawAssetData={tableProps}
          sorter={sorter || []}
        />
      )}
      <Drawer
        title={metaConfig?.search?.textMessages?.drawerFormHeader || "Search & Filter"}
        placement="right"
        size="default"
        open={filterOpen}
        onClose={() => switchFilterForm(false)}
      >
        {entityMetadata?.data && (
          <SawAssetFilter
            formProps={searchFormProps}
            metadata={entityMetadata?.data}
            onApplyFilter={() => {
              switchFilterForm(false);
              searchFormProps.form?.submit();
            }}
          />
        )}
      </Drawer>
    </List>
  );
};
